// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 16-api-conventions.md
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

import { api } from '@/lib/api';
import type { FeedbackListItem } from '@greenlight/contract';
import type { FeedbackCategory, FeedbackSource, FeedbackStatus } from '@greenlight/shared';

const PAGE_SIZE = 25;
const FEEDBACK_LIST_KEY = 'feedback-list';

export interface FeedbackFilters {
  source: FeedbackSource | '';
  status: FeedbackStatus | '';
  category: FeedbackCategory | '';
}

export interface UseFeedbackListResult {
  items: FeedbackListItem[];
  isLoading: boolean;
  isError: boolean;
  filters: FeedbackFilters;
  setFilter: (key: keyof FeedbackFilters, value: string) => void;
  hasFilters: boolean;
  clearFilters: () => void;
  nextCursor: string | null;
  canGoPrev: boolean;
  goNext: () => void;
  goPrev: () => void;
}

function parseStack(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * URL-synced filter + cursor-stack state for the feedback triage list (docs/28
 * §/feedback). Filters and the current page live in the query string so a view
 * is shareable; setting any filter drops back to page one. Cursor pagination
 * follows docs/16 (Prev/Next over an opaque server cursor).
 */
export function useFeedbackList(): UseFeedbackListResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const source = (searchParams.get('source') ?? '') as FeedbackFilters['source'];
  const status = (searchParams.get('status') ?? '') as FeedbackFilters['status'];
  const category = (searchParams.get('category') ?? '') as FeedbackFilters['category'];
  const cursor = searchParams.get('cursor') ?? undefined;
  const cursorStack = parseStack(searchParams.get('pstack'));

  const updateParams = React.useCallback(
    (mutate: (p: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setFilter = React.useCallback(
    (key: keyof FeedbackFilters, value: string) => {
      updateParams((p) => {
        if (value) p.set(key, value);
        else p.delete(key);
        p.delete('cursor');
        p.delete('pstack');
      });
    },
    [updateParams],
  );

  const clearFilters = React.useCallback(() => {
    updateParams((p) => {
      for (const k of ['source', 'status', 'category', 'cursor', 'pstack']) p.delete(k);
    });
  }, [updateParams]);

  const query = {
    limit: PAGE_SIZE,
    ...(source ? { source } : {}),
    ...(status ? { status } : {}),
    ...(category ? { category } : {}),
    ...(cursor ? { cursor } : {}),
  };
  const key = [FEEDBACK_LIST_KEY, source, status, category, cursor ?? null];
  const { data, isLoading, isError } = api.feedback.list.useQuery(
    key,
    { query },
    { queryKey: key },
  );

  const items = data?.status === 200 ? data.body.items : [];
  const nextCursor = data?.status === 200 ? data.body.next_cursor : null;

  const goNext = React.useCallback(() => {
    if (!nextCursor) return;
    const nextStack = [...cursorStack, cursor ?? ''];
    updateParams((p) => {
      p.set('cursor', nextCursor);
      p.set('pstack', JSON.stringify(nextStack));
    });
  }, [nextCursor, cursorStack, cursor, updateParams]);

  const goPrev = React.useCallback(() => {
    if (cursorStack.length > 0) {
      const prev = cursorStack[cursorStack.length - 1]!;
      const nextStack = cursorStack.slice(0, -1);
      updateParams((p) => {
        if (prev) p.set('cursor', prev);
        else p.delete('cursor');
        if (nextStack.length) p.set('pstack', JSON.stringify(nextStack));
        else p.delete('pstack');
      });
      return;
    }
    updateParams((p) => {
      p.delete('cursor');
      p.delete('pstack');
    });
  }, [cursorStack, updateParams]);

  return {
    items,
    isLoading,
    isError,
    filters: { source, status, category },
    setFilter,
    hasFilters: source !== '' || status !== '' || category !== '',
    clearFilters,
    nextCursor,
    canGoPrev: cursorStack.length > 0 || cursor !== undefined,
    goNext,
    goPrev,
  };
}
